import type { ModelManager } from '../models/ModelManager';
import type { ThemeController } from '../theme/ThemeController';
import { ModelList } from './ModelList';
import type { BoxFace } from '../section/BoxCutController';

export interface AppShellElements {
  viewport: HTMLElement;
  fitButton: HTMLButtonElement;
}

export class AppShell {
  readonly elements: AppShellElements;
  private dragDepth = 0;
  private readonly dropOverlay: HTMLElement;
  private readonly emptyState: HTMLElement;
  private readonly status: HTMLElement;
  private readonly selectionMenu: HTMLElement;
  private readonly loadingOverlay: HTMLElement;
  private readonly loadingBar: HTMLElement;
  private readonly loadingPercent: HTMLElement;
  private loadingTimer: number | null = null;
  private loadingProgress = 0;
  private readonly sectionGizmo: HTMLElement;
  private sectionProjection: { axisX: number; axisY: number; pixelsPerUnit: number } | null = null;
  private readonly boxGizmo: HTMLElement;

  constructor(
    root: HTMLElement,
    modelManager: ModelManager,
    onFiles: (files: FileList | File[]) => void,
    themeController: ThemeController,
  ) {
    root.innerHTML = `
      <div class="app-shell">
        <header class="topbar">
          <div class="brand"><span class="brand__mark"><img src="./assets/section4.png" alt="" /></span><span>BIMTEKNİK IFC</span></div>
          <div class="view-mode" aria-label="Navigation mode">
            <button class="view-mode__button view-mode__button--active" type="button">Inspect</button>
            <button class="view-mode__button" type="button" disabled title="Walk mode will be added in a later milestone">Walk · Soon</button>
          </div>
          <div class="topbar__actions">
            <span class="privacy-badge">Browser-only processing</span>
            <button class="theme-toggle" id="theme-toggle" type="button" aria-label="Switch to light theme" title="Switch viewer theme">☀</button>
            <button class="button button--secondary" id="fit-models" type="button">Fit models</button>
            <button class="button button--primary" id="add-models" type="button">Add IFC models</button>
            <input id="file-input" type="file" accept=".ifc,application/x-step" multiple hidden />
          </div>
        </header>
        <main class="workspace">
          <aside class="sidebar">
            <div class="sidebar__header">
              <div><span class="eyebrow">FEDERATION</span><h1>Models</h1></div>
              <span class="model-count" id="model-count">0</span>
            </div>
            <div class="model-list" id="model-list"></div>
            <div class="sidebar__footer">IFC files remain on this device.</div>
          </aside>
          <section class="viewport" id="viewport">
            <div class="empty-state" id="empty-state">
              <div class="empty-state__icon"><img src="./assets/section4.png" alt="Bimteknik" /></div>
              <h2>Open your IFC federation</h2>
              <p>Drop one or more IFC files here, or choose them from your device.</p>
              <button class="button button--primary" id="empty-add" type="button">Choose IFC files</button>
              <small>Files are parsed locally and are never uploaded.</small>
            </div>
            <div class="drop-overlay" id="drop-overlay"><strong>Drop IFC files to add them</strong><span>Existing models will stay open</span></div>
            <div class="status-toast" id="status" hidden></div>
            <div class="loading-overlay" id="loading-overlay" hidden>
              <div class="loading-card"><strong>IFC model is loading</strong><span>Please wait while geometry is prepared on this device.</span><div class="loading-track"><i id="loading-bar"></i></div><b id="loading-percent">0%</b></div>
            </div>
            <div class="section-gizmo" id="section-gizmo" hidden>
              <button class="section-gizmo__handle" id="section-handle" type="button" title="Drag to move section" aria-label="Drag to move section"><img src="./assets/section4.png" alt="" draggable="false" /></button>
              <div class="section-gizmo__actions"><button id="flip-section" type="button" title="Reverse direction">↔</button><button id="clear-section" type="button" title="Remove section">×</button></div>
            </div>
            <div class="box-gizmo" id="box-gizmo" hidden>
              ${(['xmin', 'xmax', 'ymin', 'ymax', 'zmin', 'zmax'] as BoxFace[]).map((face) => `<button type="button" data-box-face="${face}" title="Drag ${face} face">${face.endsWith('min') ? '−' : '+'}${face[0]?.toUpperCase()}</button>`).join('')}
              <button class="box-gizmo__close" id="clear-box-cut" type="button" title="Remove box cut">×</button>
            </div>
            <div class="selection-menu" id="selection-menu" hidden>
              <div class="selection-menu__header"><strong>Surface selected</strong><button id="clear-selection" type="button" aria-label="Clear selection">×</button></div>
              <button class="selection-menu__action" id="focus-selection" type="button"><span>⌖</span> Focus selection</button>
              <button class="selection-menu__action" id="start-section" type="button"><span>◩</span> Section</button>
              <button class="selection-menu__action" id="start-box-cut" type="button"><span>□</span> Box Cut</button>
              <button class="selection-menu__action" type="button" disabled><span>↔</span> Measure <small>Soon</small></button>
            </div>
          </section>
        </main>
      </div>
    `;

    const viewport = this.required<HTMLElement>(root, '#viewport');
    const input = this.required<HTMLInputElement>(root, '#file-input');
    const addButton = this.required<HTMLButtonElement>(root, '#add-models');
    const emptyAddButton = this.required<HTMLButtonElement>(root, '#empty-add');
    const fitButton = this.required<HTMLButtonElement>(root, '#fit-models');
    const themeButton = this.required<HTMLButtonElement>(root, '#theme-toggle');
    this.dropOverlay = this.required(root, '#drop-overlay');
    this.emptyState = this.required(root, '#empty-state');
    this.status = this.required(root, '#status');
    this.selectionMenu = this.required(root, '#selection-menu');
    this.loadingOverlay = this.required(root, '#loading-overlay');
    this.loadingBar = this.required(root, '#loading-bar');
    this.loadingPercent = this.required(root, '#loading-percent');
    this.sectionGizmo = this.required(root, '#section-gizmo');
    this.boxGizmo = this.required(root, '#box-gizmo');
    const count = this.required(root, '#model-count');

    const renderThemeButton = (): void => {
      const light = themeController.current === 'light';
      themeButton.textContent = light ? '☾' : '☀';
      themeButton.setAttribute('aria-label', light ? 'Switch to dark theme' : 'Switch to light theme');
    };
    renderThemeButton();
    themeButton.addEventListener('click', () => themeController.toggle());
    themeController.changed.subscribe(renderThemeButton);

    new ModelList(this.required(root, '#model-list'), modelManager);
    modelManager.changed.subscribe((state) => {
      count.textContent = String(state.models.length);
      this.emptyState.hidden = state.models.length > 0;
      if (state.busyCount > 0) this.startLoadingProgress();
      else if (this.loadingProgress > 0) this.finishLoadingProgress();
    });

    const openPicker = () => input.click();
    addButton.addEventListener('click', openPicker);
    emptyAddButton.addEventListener('click', openPicker);
    input.addEventListener('change', () => {
      if (input.files?.length) onFiles(input.files);
      input.value = '';
    });

    window.addEventListener('dragenter', (event) => {
      event.preventDefault();
      this.dragDepth += 1;
      if ([...event.dataTransfer?.items ?? []].some((item) => item.kind === 'file')) {
        this.dropOverlay.classList.add('drop-overlay--visible');
      }
    });
    window.addEventListener('dragover', (event) => event.preventDefault());
    window.addEventListener('dragleave', (event) => {
      event.preventDefault();
      this.dragDepth = Math.max(0, this.dragDepth - 1);
      if (this.dragDepth === 0) this.dropOverlay.classList.remove('drop-overlay--visible');
    });
    window.addEventListener('drop', (event) => {
      event.preventDefault();
      this.dragDepth = 0;
      this.dropOverlay.classList.remove('drop-overlay--visible');
      const files = [...event.dataTransfer?.files ?? []];
      const ifcFiles = files.filter((file) => file.name.toLowerCase().endsWith('.ifc'));
      if (ifcFiles.length > 0) onFiles(ifcFiles);
      else this.showTemporaryError('Please drop files with the .ifc extension.');
    });

    this.elements = { viewport, fitButton };
  }

  showSelectionMenu(
    clientX: number,
    clientY: number,
    onFocus: () => void,
    onClear: () => void,
    onSection: () => void,
    onBoxCut: () => void,
  ): void {
    const focus = this.required<HTMLButtonElement>(this.selectionMenu, '#focus-selection');
    const clear = this.required<HTMLButtonElement>(this.selectionMenu, '#clear-selection');
    const section = this.required<HTMLButtonElement>(this.selectionMenu, '#start-section');
    const boxCut = this.required<HTMLButtonElement>(this.selectionMenu, '#start-box-cut');
    focus.onclick = onFocus;
    clear.onclick = onClear;
    section.onclick = onSection;
    boxCut.onclick = onBoxCut;
    this.selectionMenu.hidden = false;

    const viewportBounds = this.elements.viewport.getBoundingClientRect();
    const menuBounds = this.selectionMenu.getBoundingClientRect();
    const localX = clientX - viewportBounds.left + 14;
    const localY = clientY - viewportBounds.top + 14;
    this.selectionMenu.style.left = `${Math.max(12, Math.min(localX, viewportBounds.width - menuBounds.width - 12))}px`;
    this.selectionMenu.style.top = `${Math.max(12, Math.min(localY, viewportBounds.height - menuBounds.height - 12))}px`;
  }

  hideSelectionMenu(): void {
    this.selectionMenu.hidden = true;
  }

  bindSectionControls(
    onOffset: (offset: number) => void,
    onFlip: () => void,
    onClear: () => void,
  ): void {
    const handle = this.required<HTMLButtonElement>(this.sectionGizmo, '#section-handle');
    let drag: { id: number; x: number; y: number; offset: number } | null = null;
    handle.addEventListener('pointerdown', (event) => {
      if (!this.sectionProjection) return;
      event.preventDefault();
      handle.setPointerCapture(event.pointerId);
      drag = { id: event.pointerId, x: event.clientX, y: event.clientY, offset: Number(handle.dataset.offset ?? 0) };
      this.elements.viewport.classList.add('viewport--section-dragging');
      this.elements.viewport.dispatchEvent(new CustomEvent('sectiondragstate', { detail: true }));
    });
    handle.addEventListener('pointermove', (event) => {
      if (!drag || drag.id !== event.pointerId || !this.sectionProjection) return;
      const dx = event.clientX - drag.x;
      const dy = event.clientY - drag.y;
      const pixels = dx * this.sectionProjection.axisX + dy * this.sectionProjection.axisY;
      onOffset(drag.offset + pixels / this.sectionProjection.pixelsPerUnit);
    });
    const finishDrag = (): void => {
      if (!drag) return;
      drag = null;
      this.elements.viewport.classList.remove('viewport--section-dragging');
      this.elements.viewport.dispatchEvent(new CustomEvent('sectiondragstate', { detail: false }));
    };
    handle.addEventListener('pointerup', finishDrag);
    handle.addEventListener('pointercancel', finishDrag);
    this.required<HTMLButtonElement>(document, '#flip-section').onclick = onFlip;
    this.required<HTMLButtonElement>(document, '#clear-section').onclick = onClear;
  }

  updateSectionGizmo(
    position: { x: number; y: number; axisX: number; axisY: number; pixelsPerUnit: number } | null,
    offset: number,
  ): void {
    this.sectionGizmo.hidden = !position;
    if (!position) return;
    this.sectionProjection = position;
    this.sectionGizmo.style.transform = `translate3d(${position.x}px, ${position.y}px, 0)`;
    this.required<HTMLButtonElement>(this.sectionGizmo, '#section-handle').dataset.offset = String(offset);
  }

  bindBoxCutControls(onMove: (face: BoxFace, delta: number) => void, onClear: () => void): void {
    for (const button of this.boxGizmo.querySelectorAll<HTMLButtonElement>('[data-box-face]')) {
      let drag: { id: number; x: number; y: number; axisX: number; axisY: number; pixelsPerUnit: number } | null = null;
      button.addEventListener('pointerdown', (event) => {
        event.preventDefault();
        button.setPointerCapture(event.pointerId);
        drag = { id: event.pointerId, x: event.clientX, y: event.clientY, axisX: Number(button.dataset.axisX), axisY: Number(button.dataset.axisY), pixelsPerUnit: Number(button.dataset.pixelsPerUnit) };
        this.elements.viewport.dispatchEvent(new CustomEvent('sectiondragstate', { detail: true }));
      });
      button.addEventListener('pointermove', (event) => {
        if (!drag || drag.id !== event.pointerId) return;
        const pixels = (event.clientX - drag.x) * drag.axisX + (event.clientY - drag.y) * drag.axisY;
        onMove(button.dataset.boxFace as BoxFace, pixels / drag.pixelsPerUnit);
        drag.x = event.clientX; drag.y = event.clientY;
      });
      const finish = (): void => { if (drag) this.elements.viewport.dispatchEvent(new CustomEvent('sectiondragstate', { detail: false })); drag = null; };
      button.addEventListener('pointerup', finish); button.addEventListener('pointercancel', finish);
    }
    this.required<HTMLButtonElement>(this.boxGizmo, '#clear-box-cut').onclick = onClear;
  }

  updateBoxGizmo(handles: Array<{ face: BoxFace; x: number; y: number; axisX: number; axisY: number; pixelsPerUnit: number }>): void {
    this.boxGizmo.hidden = handles.length === 0;
    for (const handle of handles) {
      const button = this.required<HTMLButtonElement>(this.boxGizmo, `[data-box-face="${handle.face}"]`);
      button.style.transform = `translate3d(${handle.x}px, ${handle.y}px, 0)`;
      button.dataset.axisX = String(handle.axisX); button.dataset.axisY = String(handle.axisY); button.dataset.pixelsPerUnit = String(handle.pixelsPerUnit);
    }
  }

  private startLoadingProgress(): void {
    if (this.loadingTimer !== null) return;
    this.loadingProgress = 2;
    this.renderLoadingProgress();
    this.loadingOverlay.hidden = false;
    this.loadingTimer = window.setInterval(() => {
      const remaining = 60 - this.loadingProgress;
      this.loadingProgress = Math.min(60, this.loadingProgress + Math.max(0.15, remaining * 0.035));
      this.renderLoadingProgress();
    }, 350);
  }

  private finishLoadingProgress(): void {
    if (this.loadingTimer !== null) window.clearInterval(this.loadingTimer);
    this.loadingTimer = null;
    this.loadingProgress = 100;
    this.renderLoadingProgress();
    window.setTimeout(() => {
      this.loadingOverlay.hidden = true;
      this.loadingProgress = 0;
    }, 550);
  }

  private renderLoadingProgress(): void {
    const percent = Math.round(this.loadingProgress);
    this.loadingBar.style.width = `${percent}%`;
    this.loadingPercent.textContent = `${percent}%`;
  }

  showTemporaryError(message: string): void {
    this.status.textContent = message;
    this.status.classList.add('status-toast--error');
    this.status.hidden = false;
    window.setTimeout(() => {
      this.status.classList.remove('status-toast--error');
      this.hideStatus();
    }, 5000);
  }

  private hideStatus(): void {
    this.status.hidden = true;
  }

  private required<T extends Element>(root: ParentNode, selector: string): T {
    const element = root.querySelector<T>(selector);
    if (!element) throw new Error(`Missing UI element: ${selector}`);
    return element;
  }
}

